<template>
  <div>
    <px-view-header title="Cadastro de cotação"
                    title-class="font-weight-bold"
                    :breadcrumbs="breadcrumbs">
      <template v-slot:before>
        <v-btn class="mb-4 mr-1" icon x-large color="primary" @click="onClickBack">
          <v-icon>
            mdi-arrow-left
          </v-icon>
        </v-btn>
      </template>
    </px-view-header>

    <div v-if="loadingForm">
      <px-circular-loading/>
    </div>

    <v-stepper class="elevation-1 rounded-card" v-if="!loadingForm" v-model="currentStep" vertical>

      <v-stepper-step :complete="currentStep > 1" step="1">
        Dados básicos
      </v-stepper-step>
      <v-stepper-content step="1">
        <px-cadastro-cotacao-dados-basicos :cotacao="cotacao"
                                           :is-from-requisicao="hasAnyItemFromRequisicao"
                                           :campos-readonly="camposReadonly"
                                           :campos-obrigatorios="camposObrigatorios"
                                           @ready="next"
                                           @cancel="onClickCancelar"/>
      </v-stepper-content>

      <v-stepper-step :complete="currentStep > 2" step="2">Produtos</v-stepper-step>
      <v-stepper-content step="2">
        <px-cadastro-cotacao-produtos-table :cotacao="cotacao"
                                            :is-from-requisicao="hasAnyItemFromRequisicao"
                                            :campos-readonly="camposReadonly"
                                            :campos-obrigatorios="camposObrigatorios"
                                            :hide-acoes="hideAcoes"
                                            :erp-campos-integracao="erpCamposIntegracao"
                                            @next="next"
                                            @savedraft="saveDraft"
                                            @previous="previous"/>
      </v-stepper-content>

      <v-stepper-step :complete="currentStep > 3" step="3">Fornecedores</v-stepper-step>
      <v-stepper-content step="3">
        <px-cadastro-cotacao-fornecedores v-if="currentStep === 3"
                                          :cotacao="cotacao"
                                          :loading="loading"
                                          :is-editing="isRascunho"
                                          @previous="previous"
                                          @save-draft="saveDraft"
                                          @enviar-cotacao="enviarCotacao"
                                          @reset-fornecedor="resetFornecedor"/>
      </v-stepper-content>
    </v-stepper>
    <px-confirm-dialog ref="confirm"/>
  </div>
</template>
<script>
import { mapGetters } from 'vuex';
import { cloneDeep } from 'lodash';
import CompradorService from '@/api/comprador-service';
import { deepNotEquals } from '@/utils/object-utils';
import { SituacaoCotacao, ErpCamposIntegracao } from 'px-business-components';
import EntidadeCamposCotacaoService from '@/modules/erp-campos/entidade-campos-cotacao-service';
import { buildCadastrosAdicionaisPayload } from '@/utils/cadastros-adicionais-utils';
import TiposEntidadeCadastroAdicional from '@/enums/tipos-entidade-cadastro-adicional';
import {
  computeInitialCopia,
  computeInitialRascunho,
  initialState,
} from './utils/cotacao-utils';
import CotacaoService from '../cotacao-service';
import CadastroCotacaoDadosBasicos from './CadastroCotacaoDadosBasicos.vue';
import CadastroCotacaoFornecedores from './fornecedores/CadastroCotacaoFornecedores.vue';
import CadastroCotacaoProdutosTable from './produtos/CadastroCotacaoProdutosTable.vue';

export default {
  data() {
    return {
      // todo: verificar se será necessário usar isso
      startedFornecedor: false,
      isRascunho: this.$route.params.isRascunho,
      isCopy: this.$route.params.isCopy,
      idCotacao: this.$route.params.id,
      loadingForm: false,
      loading: false,
      cotacao: null,
      initialCotacao: {},
      breadcrumbs: [
        { text: 'Gestão de compras' },
        {
          text: 'Cotações',
          to: '/cotacoes',
        },
        { text: 'Cadastro de cotação' },
      ],
      currentStep: 1,
      camposReadonly: null,
      camposObrigatorios: null,
    };
  },
  computed: {
    ...mapGetters('entidade', ['erpCamposIntegracao']),
    confirmMessage() {
      return this.isRascunho ? 'Você deseja atualizar o rascunho?' : 'Você deseja salvar a cotação como rascunho?';
    },
    isFormDirty() {
      if (this.loadingForm) {
        return false;
      }
      return Object.keys(this.initialCotacao)
        .some(key => deepNotEquals(this.cotacao, this.initialCotacao, key));
    },
    hasAnyItemFromRequisicao() {
      return this.cotacao.produtos.some(produto => !!produto?.requisicaoProduto?.id);
    },
    hideAcoes() {
      return this.erpCamposIntegracao === ErpCamposIntegracao.keys.SENIOR_MEGA.key
        && this.hasAnyItemFromRequisicao;
    },
  },
  methods: {
    next() {
      this.currentStep = this.currentStep + 1;
    },
    previous() {
      this.currentStep = this.currentStep - 1;
    },
    buildCotacaoBody() {
      const cotacao = { ...this.cotacao };
      cotacao.situacao = SituacaoCotacao.keys.ABERTA.key;
      cotacao.produtos = cotacao.produtos.map(produto => ({
        ...produto,
        situacao: SituacaoCotacao.keys.ABERTA.key,
        cadastrosAdicionais: buildCadastrosAdicionaisPayload(
          produto.cadastrosAdicionais,
          TiposEntidadeCadastroAdicional.keys.COTACAO_PRODUTO.key,
        ),
      }));
      return cotacao;
    },
    buildCotacaoDraft() {
      const cotacao = { ...this.cotacao };
      cotacao.situacao = SituacaoCotacao.keys.RASCUNHO.key;
      cotacao.projeto = cotacao.projeto || null;
      cotacao.centroCusto = cotacao.centroCusto || null;
      cotacao.formaPagamento = cotacao.formaPagamento || null;
      cotacao.prazoPagamento = cotacao.prazoPagamento || null;
      cotacao.produtos = cotacao.produtos
        .filter(produto => !!produto.unidadeMedida)
        .map(produto => ({
          ...produto,
          situacao: SituacaoCotacao.keys.RASCUNHO.key,
          cadastrosAdicionais: buildCadastrosAdicionaisPayload(
            produto.cadastrosAdicionais,
            TiposEntidadeCadastroAdicional.keys.COTACAO_PRODUTO.key,
          ),
        }));
      return cotacao;
    },
    onClickBack() {
      this.$refs.confirm.open(this.confirmMessage,
        'O rascunho ficara disponível na aba RASCUNHOS e TODAS na listagem de cotações.',
        { width: 450 })
        .then((confirm) => {
          if (!confirm) {
            this.$router.push({ name: 'main.cotacoes' });
            return;
          }
          this.saveDraft();
        });
    },
    onClickCancelar() {
      this.$router.push({ name: 'main.cotacoes' });
    },
    resetFornecedor() {
      this.cotacao.fornecedores = [];
    },
    setInitialCotacao() {
      this.initialCotacao = cloneDeep(this.cotacao);
    },
    create(data) {
      if (this.isRascunho) {
        this.cotacao = computeInitialRascunho(data, this.erpCamposIntegracao);
        this.setInitialCotacao();
      }
      if (this.isCopy) {
        this.cotacao = computeInitialCopia(data, this.erpCamposIntegracao);
        this.setInitialCotacao();
      }
    },
    getCotacao() {
      this.loadingForm = true;
      CotacaoService.getCotacao(this.idCotacao)
        .then((response) => {
          this.create(response.data);
        })
        .finally(() => {
          this.loadingForm = false;
        });
    },
    enviarCotacao() {
      this.loading = true;
      CotacaoService.save(this.buildCotacaoBody())
        .then(() => {
          this.$toast('Cotação enviada com sucesso', { color: 'success' });
          this.$router.push({ name: 'main.cotacoes' });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    saveDraft() {
      this.loading = true;
      CotacaoService.saveRascunho(this.buildCotacaoDraft())
        .then(() => {
          this.$toast('Rascunho salvo com sucesso', { color: 'success' });
          this.$router.push({ name: 'main.cotacoes' });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    getCompradorDefaultFaturamento() {
      return CompradorService.getDefaultFaturamento()
        .then((response) => {
          this.cotacao.comprador = response.data;
        });
    },
    getCompradorDefaultEntrega() {
      return CompradorService.getDefaultEntrega()
        .then((response) => {
          this.cotacao.locaisEntrega = response.data;
        });
    },
    getCamposReadonly() {
      EntidadeCamposCotacaoService.getCamposReadonly(this.erpCamposIntegracao)
        .then((data) => {
          this.camposReadonly = data;
        });
    },
    getCamposObrigatorios() {
      return EntidadeCamposCotacaoService.getCamposObrigatorios(this.erpCamposIntegracao)
        .then((data) => {
          this.camposObrigatorios = data;
        });
    },
    beforeWindowUnload(e) {
      if (this.isFormDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    },
  },
  created() {
    this.cotacao = { ...initialState(this.erpCamposIntegracao) };
    this.getCamposReadonly();
    this.getCamposObrigatorios();
    window.addEventListener('beforeunload', this.beforeWindowUnload);

    if (this.idCotacao) {
      this.getCotacao();
      return;
    }

    this.loadingForm = true;
    Promise.all([
      this.getCompradorDefaultEntrega(),
      this.getCompradorDefaultFaturamento(),
      this.getCamposReadonly(),
      this.getCamposObrigatorios(),
    ])
      .finally(() => {
        this.setInitialCotacao();
        this.loadingForm = false;
      });
  },
  beforeDestroy() {
    window.removeEventListener('beforeunload', this.beforeWindowUnload);
  },
  components: {
    pxCadastroCotacaoDadosBasicos: CadastroCotacaoDadosBasicos,
    pxCadastroCotacaoProdutosTable: CadastroCotacaoProdutosTable,
    pxCadastroCotacaoFornecedores: CadastroCotacaoFornecedores,
  },
  watch: {
    currentStep(newValue, oldValue) {
      if (newValue === oldValue) {
        return;
      }
      this.startedFornecedor = newValue === 3;
    },
  },
};
</script>
